import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { Grid, MenuItem, TextField, Typography } from '@mui/material';
import MainCard from 'ui-component/cards/MainCard';
import LoaderCircular from 'ui-component/LoaderCircular';
import Chart from 'react-apexcharts';
import '../Dashboard/styleSearch.scss';
import { gridSpacing } from 'store/constant';
import { color } from '@mui/system';

const metricsLabels = [
  { value: 'clicks', label: 'Clicks' },
  { value: 'impressions', label: 'Impressions' },
  { value: 'installs', label: 'Installs' },
  { value: 'kpi_event_count', label: 'KPI Event Count' }
];

function formatNumberAbbreviated(value, args = 1) {
  if (value == 0) return 0;
  const suffixes = ['K', 'M', 'B', 'T', 'P', 'E'];
  if (!value || Number.isNaN(value)) return 0;
  if (value < 1000) return value;

  const exp = Math.floor(Math.log(value) / Math.log(1000));
  return (value / Math.pow(1000, exp)).toFixed(args) + suffixes[exp - 1];
}

const WowNonSkanView = ({ wowReportData, loading, error, timeZone, isDollar, advertiser_campaign_fetching }) => {
  const [options, setOptions] = useState();
  const [allSeries, setAllSeries] = useState([]);
  const [selectedMetric, setSelectedMetric] = useState('installs');
  const [selectedCampaigns, setSelectedCampaigns] = useState(new Set());
  const chartRef = useRef(null);

  // Combined loading state
  const isLoading = loading || advertiser_campaign_fetching;

  // Zoom prevention effect
  useEffect(() => {
    const chartElement = chartRef.current;
    if (chartElement) {
      const preventZoom = (e) => {
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
        }
      };

      const preventWheelZoom = (e) => {
        if (e.ctrlKey) {
          e.preventDefault();
        }
      };

      document.addEventListener('wheel', preventWheelZoom, { passive: false });
      document.addEventListener('keydown', preventZoom);

      return () => {
        document.removeEventListener('wheel', preventWheelZoom);
        document.removeEventListener('keydown', preventZoom);
      };
    }
  }, []);

  // Main chart data processing effect
  useEffect(() => {
    if (!wowReportData || wowReportData.length === 0) return;

    // Initialize selected campaigns if none
    let campaignsToUse = selectedCampaigns;
    if (selectedCampaigns.size === 0) {
      campaignsToUse = new Set(wowReportData.map((item) => item.campaign_name));
      setSelectedCampaigns(campaignsToUse);
    }

    // Sort by year/week
    const sortedData = [...wowReportData].sort((a, b) => {
      if (a.year === b.year) return parseInt(a.week_number) - parseInt(b.week_number);
      return a.year - b.year;
    });

    // Group data by week-year
    const groupedData = sortedData.reduce((acc, item) => {
      const weekYear = `Week ${item.week_number}-${item.year}`;
      if (!acc[weekYear]) acc[weekYear] = { metricData: {}, media_cost: 0 };

      if (campaignsToUse.has(item.campaign_name)) {
        if (!acc[weekYear].metricData[item.campaign_name]) {
          acc[weekYear].metricData[item.campaign_name] = 0;
        }
        acc[weekYear].metricData[item.campaign_name] += item[selectedMetric] || 0;
        acc[weekYear].media_cost += isDollar ? item.media_cost || 0 : item.media_cost_local_currency || 0;
      }
      return acc;
    }, {});

    const weeks = Object.keys(groupedData);

    // Metric (bars) series → always yAxisIndex 0 (left)
    // const metricSeries = Object.keys(
    //   groupedData[weeks[0]]?.metricData || {}
    // ).map((campaignName) => ({
    //   name: campaignName,
    //   type: "column",
    //   yAxisIndex: 0,
    //   data: weeks.map((w) => groupedData[w]?.metricData[campaignName] || 0),
    // }));

    // Media cost (line) series → always yAxisIndex 1 (right)
    // const mediaCostSeries = {
    //   name: "Media Cost",
    //   type: "line",
    //   yAxisIndex: 1,
    //   data: weeks.map((w) => groupedData[w]?.media_cost || 0),
    // };

    // // Axis scaling
    // const metricMax = Math.max(...sortedData.map((item) => Number(item[selectedMetric] || 0)));
    // const mediaCostMax = Math.max(...sortedData.map((item) => Number(item.media_cost || 0)));

    // const showMediaAxis = mediaCostMax > 0; // hide axis if all zeros

    // // Combine series
    // const allSeriesData = [...metricSeries, mediaCostSeries];

    // // Dynamic styling arrays
    // const strokeWidths = allSeriesData.map((s) => (s.type === "line" ? 4 : 0));
    // const markerSizes = allSeriesData.map((s) => (s.type === "line" ? 4 : 0));

    // // --- 🔥 Dynamic colors ---
    // const colorPalette = [
    //   "#FF5733",
    //   "#33FF57",
    //   "#3357FF",
    //   "#FF33A1",
    //   "#FFD700",
    //   "#00CED1",
    //   "#8A2BE2",
    //   "#DC143C",
    //   "#FF8C00",
    //   "#20B2AA",
    //   "#FF4500",
    //   "#6495ED",
    //   "#9ACD32",
    // ];

    // const campaignColors = metricSeries.map(
    //   (_, idx) => colorPalette[idx % colorPalette.length]
    // );

    // const allColors = [...campaignColors, "#1b1bfe"]; // fixed color for Media Cost line

    // const optionsDetails = {
    //   chart: {
    //     height: 350,
    //     type: "line",
    //     stacked: false,
    //     toolbar: {
    //       show: true,
    //       tools: {
    //         download: true,
    //         selection: false,
    //         zoom: false,
    //         zoomin: false,
    //         zoomout: false,
    //         pan: false,
    //         reset: false,
    //       },
    //     },
    //     zoom: { enabled: false },
    //   },
    //   stroke: { width: strokeWidths, curve: "smooth" },
    //   fill: { opacity: [1, ...Array(metricSeries.length).fill(0.85)] },
    //   dataLabels: { enabled: false },
    //   labels: weeks,
    //   markers: { size: markerSizes },
    //   xaxis: { type: "category", title: { text: "Week - Year" } },
    //   yaxis: [
    //   {
    //     // Left axis - metrics
    //     title: {
    //       text: selectedMetric.charAt(0).toUpperCase() + selectedMetric.slice(1),
    //     },
    //     min: 0,                 // start from zero
    //     max: undefined,         // auto-scale based on data
    //     tickAmount: 5,          // optional: fixes number of ticks nicely
    //     forceNiceScale: true,   // ensures nice round numbers
    //     axisBorder: { show: true, color: "#FF5733" }, // bars color
    //     labels: { formatter: (value) => formatNumberAbbreviated(value) },
    //   },
    //   {
    //     // Right axis - media cost
    //     opposite: true,
    //     title: { text: "Media Cost" },
    //     min: 0,
    //     max: undefined,         // fully auto
    //     tickAmount: 5,          // optional, nice scaling
    //     forceNiceScale: true,
    //     show: showMediaAxis,
    //     labels: {
    //       show: showMediaAxis,
    //       formatter: (value) => formatNumberAbbreviated(value),
    //     },
    //     axisBorder: { show: showMediaAxis, color: "#1b1bfe" },
    //   },
    // ],
    //   plotOptions: { bar: { columnWidth: "70%", distributed: false } },
    //   tooltip: {
    //     shared: true,
    //     intersect: false,
    //     y: {
    //       formatter: (value, { seriesIndex, w }) =>
    //         `${formatNumberAbbreviated(value)} ${
    //           w.config.series[seriesIndex].type === "line"
    //             ? "(Media Cost)"
    //             : `(${selectedMetric})`
    //         }`,
    //     },
    //   },
    //   legend: { horizontalAlign: "left", offsetX: 40 },
    //   // colors: [
    //   //   '#FF5733', // bars (first campaign)
    //   //   '#33FF57',
    //   //   '#3357FF',
    //   //   '#FF33A1',
    //   //   '#FFD700',
    //   //   '#00CED1',
    //   //   '#8A2BE2',
    //   //   '#DC143C',
    //   //   '#FF8C00',
    //   //   '#1b1bfe' // line (media cost)
    //   // ]
    //   colors: allColors,
    // };
    // --- Build dynamic Y-axes for each campaign ---
    const campaignNames = Object.keys(groupedData[weeks[0]]?.metricData || {});

    // Series for each campaign → each gets unique yAxisIndex
    const metricSeries = campaignNames.map((campaignName, i) => ({
      name: campaignName,
      type: 'column',
      yAxisIndex: i, // unique per campaign
      data: weeks.map((w) => groupedData[w]?.metricData[campaignName] || 0)
    }));

    // Media cost line (still right side)
    const mediaCostSeries = {
      name: 'Media Cost',
      type: 'line',
      yAxisIndex: campaignNames.length, // last index
      data: weeks.map((w) => groupedData[w]?.media_cost || 0)
    };

    const allSeriesData = [...metricSeries, mediaCostSeries];

    // --- Colors ---
    const colorPalette = [
      '#FF5733',
      '#33FF57',
      '#56a6c2ff',
      '#FF33A1',
      '#FFD700',
      '#00CED1',
      '#8A2BE2',
      '#DC143C',
      '#FF8C00',
      '#20B2AA',
      '#FF4500',
      '#6495ED',
      '#9ACD32'
    ];
    const campaignColors = metricSeries.map((_, i) => colorPalette[i % colorPalette.length]);
    const allColors = [...campaignColors, '#1b1bfe']; // fixed line color

    // --- Dynamic Y-Axes (each campaign separate) ---
    const yAxes = campaignNames.map((campaignName, i) => ({
      opposite: i % 2 === 1, // alternate left/right
      title: { text: campaignName },
      labels: { formatter: (v) => formatNumberAbbreviated(v) },
      axisBorder: { show: true, color: campaignColors[i] },
      tickAmount: 5,
      forceNiceScale: true,
      min: 0
    }));

    // Add the Media Cost axis (always right)
    yAxes.push({
      opposite: true,
      title: { text: 'Media Cost' },
      labels: { formatter: (v) => formatNumberAbbreviated(v) },
      axisBorder: { show: true, color: '#1b1bfe' },
      tickAmount: 5,
      forceNiceScale: true,
      min: 0
    });

    const strokeWidths = allSeriesData.map((s) => (s.type === 'line' ? 4 : 0));
    const markerSizes = allSeriesData.map((s) => (s.type === 'line' ? 4 : 0));

    const optionsDetails = {
      chart: {
        height: 350,
        type: 'line',
        stacked: false,
        toolbar: {
          show: true,
          tools: {
            download: true,
            selection: false,
            zoom: false,
            zoomin: false,
            zoomout: false,
            pan: false,
            reset: false
          }
        },
        zoom: { enabled: false }
      },
      stroke: { width: strokeWidths, curve: 'smooth' },
      fill: { opacity: [1, ...Array(metricSeries.length).fill(0.85)] },
      dataLabels: { enabled: false },
      labels: weeks,
      markers: { size: markerSizes },
      xaxis: { type: 'category', title: { text: 'Week - Year' } },
      yaxis: yAxes, // dynamically built axes
      plotOptions: { bar: { columnWidth: '70%', distributed: false } },
      tooltip: {
        shared: true,
        intersect: false,
        y: {
          formatter: (value, { seriesIndex, w }) =>
            `${formatNumberAbbreviated(value)} ${w.config.series[seriesIndex].type === 'line' ? '(Media Cost)' : `(${selectedMetric})`}`
        }
      },
      legend: { horizontalAlign: 'left', offsetX: 40 },
      colors: allColors
    };

    setOptions(optionsDetails);
    setAllSeries(allSeriesData);
  }, [wowReportData, selectedMetric, isDollar, selectedCampaigns]);

  return (
    <MainCard>
      <Grid container spacing={gridSpacing}>
        <Grid item lg={12} md={12} sm={12} xs={12} alignContent="center" sx={{ textAlign: 'center' }}>
          <Typography variant="h4">Week-On-Week Overview</Typography>
        </Grid>

        {isLoading ? (
          <div style={{ height: '200px', width: '100%' }}>
            <LoaderCircular show={true} />
          </div>
        ) : allSeries.length === 0 || wowReportData.length === 0 ? (
          <div className="performanceDataContainer" ref={chartRef}>
            <Typography style={{ textAlign: 'center' }}>
              {error.length !== 0 ? (
                'No Data Found'
              ) : (
                <span>
                  Please click <b>Apply Filter</b> to view data
                </span>
              )}
            </Typography>
          </div>
        ) : (
          <>
            <Grid item lg={12} md={12} sm={12} xs={12} alignContent="center" sx={{ textAlign: 'center' }}>
              <Grid container direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                <Grid item>
                  <Typography variant="h3">Metrics</Typography>
                </Grid>
                <Grid item>
                  <TextField
                    id="standard-select-currency"
                    select
                    value={selectedMetric}
                    onChange={(e) => setSelectedMetric(e.target.value)}
                  >
                    {metricsLabels.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <div className="performanceMainContainer" ref={chartRef}>
                <Chart options={options} series={allSeries} type="line" width="100%" />
              </div>
            </Grid>
          </>
        )}
      </Grid>
    </MainCard>
  );
};

const mapStateToProps = (state) => ({
  wowReportData: state.wowReducer.wowReportData,
  loading: state.wowReducer.loading,
  error: state.wowReducer.error,
  timeZone: state.advertiserReducer.timeZone,
  isDollar: state.campaignReducer.isDollar,
  advertiser_campaign_fetching: state.advertiserReducer.advertiser_campaign_fetching
});

export default connect(mapStateToProps, null)(WowNonSkanView);
